/**
 * HIPAA compliance tracker.
 *
 * The checklist that used to live in docs/hipaa-checklist.md, with live status, comments and
 * an audit trail. Firestore is the source of truth; `npm run hipaa:seed` in tools/ refreshes
 * the descriptive text without touching tracked state.
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import Button from '../../components/Button';
import ErrorDisplay from '../../components/ErrorDisplay';
import LoadingSpinner from '../../components/LoadingSpinner';
import { TabNavigation, type TabConfig } from '../../components/TabNavigation';
import { ErrandsTab, TrustListTab, VendorsTab } from './components/AppendixTabs';
import { ChecklistTab } from './components/ChecklistTab';
import {
  filtersFromParams,
  filtersToParams,
  type ChecklistFilters,
} from './utils/checklistFilters';
import { HipaaOverview } from './components/HipaaOverview';
import { ItemDetailModal } from './components/ItemDetailModal';
import { useHipaaData } from './hooks/useHipaaData';
import type { HipaaItem } from './types/hipaaTypes';
import { effectiveStatus } from './utils/hipaaStats';

const TABS = ['overview', 'checklist', 'verification', 'vendors', 'trust-list'] as const;
type TabId = (typeof TABS)[number];

const TAB_CONFIG: TabConfig<TabId>[] = [
  { id: 'overview', label: 'Overview', icon: '📊' },
  { id: 'checklist', label: 'Checklist', icon: '✅' },
  { id: 'verification', label: 'Verification', icon: '🔍' },
  { id: 'vendors', label: 'Vendors', icon: '🤝' },
  { id: 'trust-list', label: 'Trust list', icon: '🛡️' },
];

const isTabId = (value: string | null): value is TabId =>
  value !== null && (TABS as readonly string[]).includes(value);

export const Hipaa = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { items, activity, stats, loading, error, reload, updateItem, addComment } = useHipaaData();

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const tabParam = searchParams.get('tab');
  const activeTab: TabId = isTabId(tabParam) ? tabParam : 'overview';

  // Tab and filters both live in the query string, so any view can be pasted to someone else.
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);

  const setTab = (tab: string) => {
    setSearchParams(tab === 'overview' ? {} : { tab });
  };

  const setFilters = (next: ChecklistFilters) => {
    setSearchParams({ tab: activeTab, ...filtersToParams(next) });
  };

  const byKind = useMemo(
    () => ({
      checklist: items.filter((item) => item.kind === 'checklist'),
      errand: items.filter((item) => item.kind === 'errand'),
      vendor: items.filter((item) => item.kind === 'vendor'),
      crosswalk: items.filter((item) => item.kind === 'crosswalk'),
    }),
    [items],
  );

  // Held by id rather than by value so the modal re-renders from the patched list after a save.
  const selected = selectedId ? (items.find((item) => item.id === selectedId) ?? null) : null;

  if (loading) return <LoadingSpinner message="Loading the HIPAA checklist…" />;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <h1 className="text-3xl font-bold text-gray-900">HIPAA compliance</h1>
          <Button
            variant="outline"
            size="sm"
            icon={<RefreshCw size={13} />}
            onClick={() => void reload()}
          >
            Refresh
          </Button>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Business-associate readiness. {stats.p0Total - stats.p0Done} P0 blocker
          {stats.p0Total - stats.p0Done === 1 ? '' : 's'} before the first BAA can be signed.
        </p>

        {error && <ErrorDisplay message={error} />}

        <TabNavigation tabs={TAB_CONFIG} activeTab={activeTab} onTabChange={setTab} />

        {activeTab === 'overview' && (
          <HipaaOverview
            items={items}
            activity={activity}
            stats={stats}
            onOpenItem={(item) => setSelectedId(item.id)}
            onGoToTab={setTab}
          />
        )}

        {activeTab === 'checklist' && (
          <ChecklistTab
            items={byKind.checklist}
            allItems={items}
            filters={filters}
            onFiltersChange={setFilters}
            onOpenItem={(item) => setSelectedId(item.id)}
          />
        )}

        {activeTab === 'verification' && (
          <ErrandsTab items={byKind.errand} onOpenItem={(item) => setSelectedId(item.id)} />
        )}

        {activeTab === 'vendors' && (
          <VendorsTab items={byKind.vendor} onOpenItem={(item) => setSelectedId(item.id)} />
        )}

        {activeTab === 'trust-list' && (
          <TrustListTab
            items={byKind.crosswalk}
            allItems={items}
            onOpenItem={(item) => setSelectedId(item.id)}
          />
        )}
      </div>

      {selected && (
        <ItemDetailModal
          item={selected}
          displayStatus={effectiveStatus(selected, items)}
          onClose={() => setSelectedId(null)}
          onUpdate={updateItem}
          onAddComment={addComment}
        />
      )}
    </div>
  );
};

export type { HipaaItem };
